import { Injectable } from '@angular/core';
import {
  MessagePosition,
  MessageType,
  SnackBarService,
} from '@epicuro-next/ataraxia/snack-bar';
import {
  DocumentsApiService,
  DocumentType,
  IAccountDocument,
} from '@epicuro-next/data/documents';
import { selectTermsConditionsUserconsent } from '@epicuro-next/platform/configuration';
import { setActiveAccount } from '@epicuro-next/state/accounts-state';
import { go } from '@epicuro-next/state/router-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { logout, setUnsignedUserTerms } from '../actions';

import {
  acceptUserTerms,
  acceptUserTermsFail,
  acceptUserTermsSuccess,
  allUserTermsAccepted,
} from './../actions/user-terms.actions';
import { selectUnsignedUserTerms } from './../user-state.selectors';

@Injectable()
export class UserTermsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly documentsApi: DocumentsApiService,
    private readonly snackBar: SnackBarService,
    private readonly store: Store,
  ) {}

  private termsConditionsUserconsent$ = this.store.pipe(
    select(selectTermsConditionsUserconsent),
  );

  public checkUserTerms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveAccount),
      concatLatestFrom(() => [this.termsConditionsUserconsent$]),
      switchMap(([{ account }, termsConditionsUserconsent]) => {
        const accountId = account.accountId;
        const getTermsAndConditionsDocuments$ = termsConditionsUserconsent
          ? this.documentsApi.getDocumentsByTypeAndAccountId(
              DocumentType.TermsAndConditions,
              accountId,
            )
          : of([]);
        const getDocuments$ = this.documentsApi
          .getAccountDocuments(accountId)
          .pipe(
            switchMap((accountDocuments) =>
              getTermsAndConditionsDocuments$.pipe(
                map((termsAndConditionsDocuments) =>
                  accountDocuments
                    .concat(termsAndConditionsDocuments)
                    .filter((document) => !document.lastAcceptedDateTime),
                ),
              ),
            ),
            map((documents) => setUnsignedUserTerms({ documents })),
            catchError(() => of(logout())),
          );
        return getDocuments$;
      }),
    ),
  );

  public acceptUserTerms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(acceptUserTerms),
      switchMap(({ document }) =>
        this.documentsApi
          .postAcceptDocument(document.id, document.documentVersionId, null)
          .pipe(map(() => document)),
      ),
      map((document) => acceptUserTermsSuccess({ document })),
      catchError(() => of(acceptUserTermsFail())),
    ),
  );

  public acceptUserTermsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(acceptUserTermsSuccess),
        tap(({ document }) => {
          this.snackBar.show(
            $localize`:@@termsAndConditionsAcceptDocumentSuccessFeedback:You successfuly accepted ${document.name}.`,
            MessagePosition.BOTTOM,
            MessageType.SUCCESS,
          );
        }),
      ),
    { dispatch: false },
  );

  public acceptUserTermsFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(acceptUserTermsFail),
        tap(() => {
          this.snackBar.show(
            $localize`:@@termsAndConditionsAcceptDocumentFailFeedback:There was an error while accepting this document. Please try again.`,
            MessagePosition.BOTTOM,
            MessageType.DANGER,
          );
        }),
      ),
    { dispatch: false },
  );

  public allUserTermsAccepted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(allUserTermsAccepted),
      withLatestFrom(this.store.pipe(select(selectUnsignedUserTerms))),
      filter(([, documents]) => documents?.length === 0),
      switchMap(() => of(go({ commands: ['/'] }))),
    ),
  );

  public clearUnsignedDocumentsForGuard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveAccount),
      switchMap(() =>
        of(
          setUnsignedUserTerms({
            documents: null as unknown as ReadonlyArray<IAccountDocument>,
          }),
        ),
      ),
    ),
  );
}
